import React, { useState, useEffect } from 'react';
import { Col, Row } from '@thd-olt-component-react/core-ui';
import { useParams, useNavigate, useLocation } from '@thd-olt-component-react/router';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { useConfigService } from '@thd-nucleus/experience-context';
import { observer } from 'mobx-react-lite';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import { getCandidateById, updateHiredStatus } from '../../api/SlmService';
import { Spinner } from '../../components/common/Spinner/Spinner';
import ProfileOverviewSection from './ProfileOverviewSection';
import ProfileMarkAsHiredSection from './ProfileMarkAsHiredSection';
import ProfileHighlightsSection from './ProfileHighlightsSection';
import ProfileInviteCandidateSection from './ProfileInviteCandidate/ProfileInviteCandidateSection';
import InviteCandidateModal from './ProfileInviteCandidate/InviteCandidateModal';
import TermsAndConditions from '../../components/common/TermsAndConditions/TermsAndConditions';
import { FlagContent } from '../../components/ProfileSearch/ProfileDetails/FlagContent/FlagContent';
import { EXP_ROUTES, HOME_PAGE_PATH } from '../../utils/constants';
import { useStore } from '../../store';
import ProfileMyWorkSection from './ProfileMyWorkSection';
import { pageLoadEvents, clickEvents } from '../../utils/analytics';
import JobPostingNotification from '../JobPostings/JobPostingPage/JobPostingDetails/JobPostingNotification';

// todo place profile details in store and fix back button state persistence
const ProfileDetails = () => {

  const { candidateId, jobPostingId } = useParams();

  const navigate = useNavigate();
  const location = useLocation();

  const {
    notificationStore: {
      setError
    },
    authStore: {
      authorized,
      authInProgress
    }
  } = useStore();

  const {
    bulkJobInvitationStore: {
      fetchActiveJobPostings,
      toggleBulkJobInvitationOpen,
      bulkJobInvitationAvailable
    },
    bulkJobInvitationNotificationStore: {
      bulkJobInvitationSentNotificationVisible
    }
  } = useStore();

  const [profile, setProfile] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const isBulkInviteModalEnabled = useConfigService('fs:isBulkInviteModalEnabled');

  let backToObj;
  if (location.pathname.includes('search')) {
    backToObj = {
      backTo: 'Search',
      backToUrl: EXP_ROUTES.CANDIDATE_SEARCH
    };
  } else if (location.pathname.includes('applicants')) {
    backToObj = {
      backTo: 'Applicants',
      backToUrl: `${EXP_ROUTES.JOB_APPLICANTS(jobPostingId)}`
    };
  } else if (location.pathname.includes('dashboard')) {
    backToObj = {
      backTo: 'Dashboard',
      backToUrl: `${EXP_ROUTES.MY_DASHBOARD}`
    };
  }

  const fetchCandidateDetails = async (currCandidateId) => {
    setIsLoading(true);
    try {
      const data = await getCandidateById(currCandidateId);
      setProfile(data);
    } catch (error) {
      setError('Failed to fetch candidate profile details. Please try again.');
      navigate(backToObj.backToUrl);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (candidateId) {
      fetchCandidateDetails(candidateId);
      (isBulkInviteModalEnabled && fetchActiveJobPostings(candidateId));
    }
  }, [candidateId]);

  useEffect(() => {
    if (!authInProgress && !authorized) {
      window.location.replace(`${HOME_PAGE_PATH}`);
    } else {
      let breadcrumb;
      if (location.pathname.includes('search')) {
        breadcrumb = 'candidate search';
      } else if (location.pathname.includes('applicants')) {
        breadcrumb = 'my dashboard>job applicants';
      } else if (location.pathname.includes('dashboard')) {
        breadcrumb = 'my dashboard';
      }
      pageLoadEvents.candidatePage(breadcrumb);
    }
  }, []);

  function toggleProfileStateStatusCB(prevProfile) {
    clickEvents.candidatePageInteraction(
      'hiring status',
      !prevProfile.candidateHired ? 'hired' : 'not hired',
      prevProfile.candidateId
    );
    return {
      ...prevProfile,
      candidateHired: !prevProfile.candidateHired
    };
  }

  const handleHiredToggle = () => {
    setProfile(toggleProfileStateStatusCB);

    updateHiredStatus(profile.candidateId, !profile.candidateHired)
      .catch(() => {
        setProfile(toggleProfileStateStatusCB);
      });
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <main className="p2p-profile">
      <ErrorBoundary name="profile-details-heading">
        <PageHeader>{profile.name}</PageHeader>
      </ErrorBoundary>
      <Row className="p2p-profile">
        <ErrorBoundary name="p2p-profile">
          <ProfileOverviewSection profile={profile} updateHireStatus={handleHiredToggle} />
          <Col sm={9} md={9} xs={12}>
            {/* Hired info */}
            <div className="p2p-profile__top-bar">
              {isBulkInviteModalEnabled && bulkJobInvitationAvailable && <ProfileInviteCandidateSection openModal={toggleBulkJobInvitationOpen} />}
              <ProfileMarkAsHiredSection profile={profile} updateHireStatus={handleHiredToggle} />
            </div>
            <ProfileMyWorkSection profile={profile} />
            <ProfileHighlightsSection profile={profile} />
            <Row />
          </Col>
        </ErrorBoundary>
      </Row>
      <ErrorBoundary name="flag-content">
        <FlagContent candidate={profile} />
      </ErrorBoundary>
      <ErrorBoundary name="terms-and-conditions">
        <TermsAndConditions />
      </ErrorBoundary>
      {isBulkInviteModalEnabled && (
        <ErrorBoundary name="bulk-invite-modal">
          <InviteCandidateModal candidateId={candidateId} />
          <JobPostingNotification
            visible={bulkJobInvitationSentNotificationVisible}
            title="Invitations Sent"
            text="Candidate will be notified and encouraged to apply to jobs you selected."
          />
        </ErrorBoundary>
      )}
    </main>
  );
};

export default observer(ProfileDetails);
